import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import IdentityVerification from '../components/IdentityVerification';
import InteractiveQuestion from '../components/course/InteractiveQuestion';

const HEARTBEAT_INTERVAL = 300;       // Send heartbeat every 5 minutes
// Default idle thresholds (Arizona). Kansas courses override via courseConfig.idle_threshold_minutes
// which produces a pause threshold and a "prompt earlier" threshold below it.
const DEFAULT_IDLE_PROMPT_THRESHOLD = 180;    // Prompt user after 3 minutes of inactivity
const DEFAULT_IDLE_PAUSE_THRESHOLD = 240;     // Fully pause timer after 4 minutes
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

// Format seconds as M:SS (e.g. 4:32 remaining)
const formatMinSec = (totalSec) => {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? '0' : ''}${r}`;
};

export default function CoursePlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const [slides, setSlides] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  // Per-course compliance config (Kansas KREC settings). NULL fields fall back to defaults.
  const [courseConfig, setCourseConfig] = useState(null);
  // Tracks which interactive questions the student has answered on this slide-view
  // (initial state comes from server via previousResult; this covers same-session answers)
  const [interactiveAnswered, setInteractiveAnswered] = useState({});
  // Compute effective idle thresholds. Kansas courses set idle_threshold_minutes; AZ uses defaults.
  const IDLE_PAUSE_THRESHOLD = courseConfig?.idle_threshold_minutes
    ? courseConfig.idle_threshold_minutes * 60
    : DEFAULT_IDLE_PAUSE_THRESHOLD;
  // Prompt at 75% of the pause threshold (Kansas 20 min → prompt at 15 min)
  const IDLE_PROMPT_THRESHOLD = Math.max(60, Math.round(IDLE_PAUSE_THRESHOLD * 0.75));
  const [currentIdx, setCurrentIdx] = useState(0);
  const [timers, setTimers] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);

  // Section quiz state
  const [quizData, setQuizData] = useState(null);
  const [quizResponses, setQuizResponses] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [passedQuizzes, setPassedQuizzes] = useState({});

  // Final exam state
  const [finalExam, setFinalExam] = useState(null);
  const [finalResponses, setFinalResponses] = useState({});
  const [finalResult, setFinalResult] = useState(null);
  const [finalLoading, setFinalLoading] = useState(false);
  const [showFinalExam, setShowFinalExam] = useState(false);

  // Section quiz mapping: sectionId -> quiz
  const [sectionQuizMap, setSectionQuizMap] = useState({});
  const [sectionTimers, setSectionTimers] = useState({});

  const heartbeatRef = useRef(null);
  const lastHeartbeatRef = useRef(Date.now());
  const lastActivityRef = useRef(Date.now());
  const idleCheckRef = useRef(null);
  const [idlePromptOpen, setIdlePromptOpen] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  // Live tick for displaying the section countdown
  const [, setTick] = useState(0);
  const currentSlide = slides[currentIdx];
  // Review mode: student already completed this course — unlock all navigation, skip requirements
  const isReviewMode = enrollment?.status === 'completed';

  useEffect(() => {
    const load = async () => {
      try {
        const courseRes = await api.get(`/courses/${id}`);
        setCourse(courseRes.data.course);

        let slidesRes;
        try {
          slidesRes = await api.get(`/courses/${id}/slides`);
        } catch (slideErr) {
          if (slideErr.response?.data?.error === 'DAILY_LIMIT') {
            setDailyLimitReached(true);
            setLoading(false);
            return;
          }
          throw slideErr;
        }

        const timersRes = await api.get(`/timers/${id}`);
        setEnrollment(slidesRes.data.enrollment);
        if (slidesRes.data.courseConfig) setCourseConfig(slidesRes.data.courseConfig);
        if (slidesRes.data.sessionToken) setSessionToken(slidesRes.data.sessionToken);
        const sl = slidesRes.data.slides;
        setSlides(sl);
        const tmap = {};
        timersRes.data.timers.forEach(t => { tmap[t.slide_id] = t; });
        setTimers(tmap);
        // Load section timers
        const stmap = {};
        (timersRes.data.sectionTimers || []).forEach(t => { stmap[t.section_id] = t; });
        setSectionTimers(stmap);

        const savedSlide = slidesRes.data.enrollment?.current_slide_id;
        if (savedSlide) {
          const idx = sl.findIndex(s => s.id === savedSlide);
          if (idx >= 0) setCurrentIdx(idx);
        }

        // Load section quizzes for this course
        try {
          const quizRes = await api.get(`/quizzes/course/${id}/section-quizzes`);
          const qmap = {};
          (quizRes.data.quizzes || []).forEach(q => {
            if (q.section_id) qmap[q.section_id] = q;
          });
          setSectionQuizMap(qmap);
          // Mark already passed quizzes
          const passedMap = {};
          (quizRes.data.passedQuizIds || []).forEach(qid => { passedMap[qid] = true; });
          setPassedQuizzes(passedMap);
        } catch {}

        // Load final exam info
        try {
          const examRes = await api.get(`/quizzes/course/${id}/final-exam`);
          if (examRes.data.exam) setFinalExam(examRes.data.exam);
        } catch {}

        // Check identity verification status and daily limit — skip for completed courses (review mode)
        const isReview = slidesRes.data.enrollment?.status === 'completed';
        if (!isReview) {
          try {
            const vRes = await api.get(`/verification/${id}/status`);
            setVerificationStatus(vRes.data);
            if (vRes.data.dailyLimitReached) {
              setDailyLimitReached(true);
            } else if (!vRes.data.verified) {
              setShowVerification(true);
            }
          } catch {}
        }

      } catch (err) {
        if (err.response?.status === 403) navigate('/my-courses');
      } finally { setLoading(false); }
    };
    load();
  }, [id, navigate]);

  const sendHeartbeat = useCallback(async () => {
    if (!currentSlide || sessionConflict) return;
    // Review mode: no need to track timers or session on a completed course
    if (enrollment?.status === 'completed') return;
    const now = Date.now();
    // Calculate active elapsed time — exclude any time spent idle
    const totalElapsedSec = Math.round((now - lastHeartbeatRef.current) / 1000);
    const idleSec = Math.max(0, Math.round((now - lastActivityRef.current) / 1000) - IDLE_PROMPT_THRESHOLD);
    const elapsed = Math.max(0, totalElapsedSec - idleSec);
    lastHeartbeatRef.current = now;
    if (elapsed === 0) return; // nothing to record
    try {
      const res = await api.post('/timers/heartbeat', {
        slideId: currentSlide.id, courseId: id, secondsToAdd: elapsed, sessionToken,
      });
      setTimers(prev => ({ ...prev, [currentSlide.id]: { seconds_spent: res.data.secondsSpent, is_complete: true } }));
      // Update section timer state from backend
      if (currentSlide.section_id) {
        setSectionTimers(prev => ({
          ...prev,
          [currentSlide.section_id]: {
            ...prev[currentSlide.section_id],
            is_complete: res.data.sectionTimerComplete,
            seconds_spent: res.data.sectionSecondsSpent || (prev[currentSlide.section_id]?.seconds_spent || 0) + elapsed,
            min_time_minutes: res.data.sectionMinTimeMinutes || prev[currentSlide.section_id]?.min_time_minutes || 0,
          }
        }));
      }
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.error === 'SESSION_CONFLICT') {
        setSessionConflict(true);
        clearInterval(heartbeatRef.current);
      }
    }
  }, [currentSlide, id, sessionToken, sessionConflict, enrollment?.status]);

  useEffect(() => {
    if (!currentSlide) return;
    // Review mode: don't track progress or heartbeat on a completed course
    if (enrollment?.status === 'completed') return;
    lastHeartbeatRef.current = Date.now();
    lastActivityRef.current = Date.now(); // navigating to a new slide counts as activity
    setIdlePromptOpen(false);
    setTimerPaused(false);
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL * 1000);
    api.post(`/courses/${id}/progress`, { slideId: currentSlide.id }).catch(() => {});
    // Send heartbeat when LEAVING this slide (cleanup)
    return () => { clearInterval(heartbeatRef.current); sendHeartbeat(); };
  }, [currentSlide, sendHeartbeat, id, enrollment?.status]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) { clearInterval(heartbeatRef.current); sendHeartbeat(); }
      else { lastHeartbeatRef.current = Date.now(); lastActivityRef.current = Date.now(); heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL * 1000); }
    };
    // Save progress when closing tab/browser
    const handleBeforeUnload = () => { sendHeartbeat(); };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendHeartbeat]);

  // Activity tracking — update lastActivityRef on user interaction
  useEffect(() => {
    if (enrollment?.status === 'completed') return; // skip in review mode
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      // If the idle prompt was open and the user came back, dismiss it (resume timer)
      if (idlePromptOpen) {
        setIdlePromptOpen(false);
        setTimerPaused(false);
        lastHeartbeatRef.current = Date.now();
      }
    };
    ACTIVITY_EVENTS.forEach(ev => document.addEventListener(ev, handleActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach(ev => document.removeEventListener(ev, handleActivity));
    };
  }, [idlePromptOpen, enrollment?.status]);

  // Tick every 5s — drive both the visual countdown and idle detection
  useEffect(() => {
    if (enrollment?.status === 'completed') return;
    if (!currentSlide) return;
    idleCheckRef.current = setInterval(() => {
      const idleSec = Math.round((Date.now() - lastActivityRef.current) / 1000);
      if (idleSec >= IDLE_PROMPT_THRESHOLD && !idlePromptOpen && !timerPaused) {
        // Show "Are you still there?" prompt and capture the active time so far
        sendHeartbeat();
        setIdlePromptOpen(true);
      }
      if (idleSec >= IDLE_PAUSE_THRESHOLD && !timerPaused) {
        // Fully pause timer accumulation
        setTimerPaused(true);
        clearInterval(heartbeatRef.current);
      }
      // Force re-render so the live countdown updates
      setTick(t => t + 1);
    }, 5000);
    return () => clearInterval(idleCheckRef.current);
  }, [currentSlide, idlePromptOpen, timerPaused, enrollment?.status, sendHeartbeat]);

  // Resume the timer after user confirms they're still there
  const handleStillThere = useCallback(() => {
    setIdlePromptOpen(false);
    setTimerPaused(false);
    lastActivityRef.current = Date.now();
    lastHeartbeatRef.current = Date.now();
    // Restart the heartbeat interval
    clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL * 1000);
  }, [sendHeartbeat]);

  // Auto-expand the section containing the current slide
  useEffect(() => {
    if (currentSlide?.section_id) {
      setExpandedSections(prev => ({ ...prev, [currentSlide.section_id]: true }));
    }
  }, [currentSlide?.section_id]);

  // Keyboard arrow navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showQuiz || showFinalExam || showVerification || sessionConflict || dailyLimitReached) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentIdx > 0) goToSlide(currentIdx - 1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  const loadQuiz = async (quizId) => {
    setQuizLoading(true);
    try {
      const res = await api.get(`/quizzes/section/${quizId}/questions`);
      setQuizData(res.data);
      setShowQuiz(true);
      setQuizResult(null);
      setQuizResponses({});
      if (res.data.alreadyPassed) {
        setPassedQuizzes(prev => ({ ...prev, [quizId]: true }));
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Could not load quiz');
    } finally { setQuizLoading(false); }
  };

  const submitQuiz = async () => {
    if (!quizData) return;
    const unanswered = quizData.questions.filter(q => !quizResponses[q.id]);
    if (unanswered.length > 0) { alert(`Please answer all ${unanswered.length} remaining question(s).`); return; }
    setQuizLoading(true);
    try {
      const res = await api.post(`/quizzes/section/${quizData.quiz.id}/submit`, {
        responses: quizResponses,
        userCourseId: enrollment?.id,
      });
      setQuizResult(res.data);
      if (res.data.passed) {
        setPassedQuizzes(prev => ({ ...prev, [quizData.quiz.id]: true }));
      }
    } finally { setQuizLoading(false); }
  };

  const loadFinalExam = async () => {
    if (!finalExam) return;
    setFinalLoading(true);
    try {
      const res = await api.get(`/quizzes/final/${finalExam.id}/questions`);
      setFinalExam({ ...res.data.exam, questions: res.data.questions });
      setFinalResponses({});
      setFinalResult(null);
      setShowFinalExam(true);
    } finally { setFinalLoading(false); }
  };

  const submitFinalExam = async () => {
    if (!finalExam) return;
    const unanswered = (finalExam.questions || []).filter(q => !finalResponses[q.id]);
    if (unanswered.length > 0) { alert(`Please answer all ${unanswered.length} remaining question(s).`); return; }
    setFinalLoading(true);
    try {
      const res = await api.post(`/quizzes/final/${finalExam.id}/submit`, {
        responses: finalResponses,
        userCourseId: enrollment?.id,
      });
      setFinalResult(res.data);
    } finally { setFinalLoading(false); }
  };

  // Build ordered list of unique sections and determine which are complete
  const sectionOrder = [];
  const sectionComplete = {};
  slides.forEach(s => {
    if (s.section_id && !sectionOrder.includes(s.section_id)) {
      sectionOrder.push(s.section_id);
    }
  });
  sectionOrder.forEach(secId => {
    const timer = sectionTimers[secId];
    const timerDone = timer?.is_complete || false;
    const quiz = sectionQuizMap[secId];
    const quizDone = quiz ? !!passedQuizzes[quiz.id] : true; // no quiz = auto pass
    sectionComplete[secId] = timerDone && quizDone;
  });

  // Check if a section is unlocked (all previous sections must be complete)
  const isSectionUnlocked = (sectionId) => {
    if (!sectionId) return true; // slides without a section are always accessible
    const secIdx = sectionOrder.indexOf(sectionId);
    if (secIdx <= 0) return true; // first section is always unlocked
    // All sections before this one must be complete
    for (let i = 0; i < secIdx; i++) {
      if (!sectionComplete[sectionOrder[i]]) return false;
    }
    return true;
  };

  const goToSlide = (idx) => {
    const targetSlide = slides[idx];
    // Block navigation to locked sections (only when going forward) — skip in review mode
    if (!isReviewMode && targetSlide && idx > currentIdx && targetSlide.section_id && !isSectionUnlocked(targetSlide.section_id)) {
      return; // section not unlocked yet
    }
    sendHeartbeat();
    setCurrentIdx(idx);
    setShowQuiz(false);
    setShowFinalExam(false);
    setQuizData(null);
    setQuizResult(null);
    setQuizResponses({});
  };

  // Get quiz for current slide's section
  const currentSectionQuiz = currentSlide?.section_id ? sectionQuizMap[currentSlide.section_id] : null;
  const currentQuizId = currentSectionQuiz?.id;

  // Check if current slide is the last slide in its section
  const isLastSlideInSection = currentSlide && currentSlide.section_id && (
    currentIdx === slides.length - 1 ||
    slides[currentIdx + 1]?.section_id !== currentSlide.section_id
  );

  // Section timer info
  const sectionTimer = currentSlide?.section_id ? sectionTimers[currentSlide.section_id] : null;
  const sectionMinTime = currentSlide?.section_id
    ? (slides.find(s => s.section_id === currentSlide.section_id)?.section_title ? sectionTimer : null)
    : null;
  const sectionTimeComplete = sectionTimer?.is_complete || false;

  // For sections without a section_id, always allow progression
  const timerComplete = !currentSlide?.section_id || sectionTimeComplete;
  const currentQuizPassed = currentQuizId ? passedQuizzes[currentQuizId] : true;

  // Determine if quiz is needed: only on last slide of section
  const needsQuiz = isLastSlideInSection && currentQuizId && !passedQuizzes[currentQuizId];

  // If the current slide embeds an interactive question, the student must answer it
  // before Next is enabled (Kansas KREC engagement requirement).
  const interactiveGateOpen = !currentSlide?.interactive_question_id || !!interactiveAnswered[currentSlide.interactive_question_id];

  // Can go next to next SECTION if both quiz passed AND time met
  // Students can take quiz anytime, but can't advance to next section until time is also met
  const canGoNext = (isLastSlideInSection
    ? (timerComplete && currentQuizPassed)  // last slide: need both time + quiz
    : true) && interactiveGateOpen;          // mid-section slides: always navigable unless interactive question unanswered

  // Calculate section time remaining for display (with live ticking between heartbeats)
  const sectionSecondsSpent = sectionTimer?.seconds_spent || 0;
  const sectionMinSeconds = (sectionTimer?.min_time_minutes || 0) * 60;
  // Add unposted active seconds since last heartbeat (only if user hasn't been idle)
  const liveExtra = (() => {
    if (sectionTimeComplete || timerPaused || isReviewMode) return 0;
    const since = Math.round((Date.now() - lastHeartbeatRef.current) / 1000);
    const idle = Math.max(0, Math.round((Date.now() - lastActivityRef.current) / 1000) - IDLE_PROMPT_THRESHOLD);
    return Math.max(0, since - idle);
  })();
  const sectionTimeRemaining = Math.max(0, sectionMinSeconds - sectionSecondsSpent - liveExtra);

  const handleNext = () => {
    // Review mode: free navigation, no quiz/exam/timer gates
    if (isReviewMode) {
      if (currentIdx < slides.length - 1) goToSlide(currentIdx + 1);
      return;
    }
    // If last slide in section and quiz exists but hasn't been passed, show quiz (anytime)
    if (isLastSlideInSection && currentQuizId && !passedQuizzes[currentQuizId]) {
      loadQuiz(currentQuizId);
      return;
    }
    // If last slide in section, quiz passed but time not met — block
    if (isLastSlideInSection && !timerComplete) {
      return;
    }
    // If last slide, show final exam
    if (currentIdx === slides.length - 1 && finalExam) {
      loadFinalExam();
      return;
    }
    if (currentIdx < slides.length - 1) goToSlide(currentIdx + 1);
  };

  const progress = enrollment?.progress_percent || 0;
  const completedSlides = slides.filter(s => timers[s.id]?.is_complete).length;

  if (loading) return (
    <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--slate-900)' }}>
      <div style={{ textAlign:'center', color:'white' }}>
        <div className="spinner" style={{ borderColor:'rgba(255,255,255,0.2)', borderTopColor:'var(--teal-400)' }} />
        <p style={{ marginTop:16, color:'var(--slate-400)' }}>Loading course...</p>
      </div>
    </div>
  );

  // Daily limit reached — block access
  if (dailyLimitReached) {
    return (
      <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--slate-900)' }}>
        <div style={{ textAlign:'center', color:'white', maxWidth:500, padding:40 }}>
          <div style={{ fontSize:64, marginBottom:20 }}>🚫</div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:24, marginBottom:16 }}>Daily Course Limit Reached</h2>
          <p style={{ fontSize:15, color:'rgba(255,255,255,0.7)', lineHeight:1.7, marginBottom:24 }}>
            It is a requirement of Arizona Department of Real Estate that you can only complete three (3) courses per day. Please come back tomorrow to continue your coursework.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/my-courses')} style={{ background:'#b39e89', border:'none', padding:'12px 32px' }}>
            Go to My Courses
          </button>
        </div>
      </div>
    );
  }

  // Show identity verification screen before course starts
  if (showVerification && verificationStatus) {
    return (
      <div style={styles.root}>
        <div style={styles.topBar}>
          <div style={styles.topLeft}>
            <Link to="/my-courses" style={styles.topHomeLink}>← My Courses</Link>
            <span style={styles.topTitle}>{course?.title}</span>
          </div>
        </div>
        <div style={{ flex:1, overflow:'auto', background:'var(--slate-900)' }}>
          <IdentityVerification
            courseId={id}
            courseState={course?.state}
            user={verificationStatus.user}
            selfieRequired={verificationStatus.selfieRequired}
            onVerified={() => setShowVerification(false)}
            onCancel={() => navigate('/my-courses')}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {/* Idle prompt — "Are you still there?" */}
      {idlePromptOpen && !sessionConflict && (
        <div style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'var(--slate-800)', border:'1px solid var(--slate-700)', borderRadius:16, padding:'36px 32px', maxWidth:420, width:'100%', textAlign:'center', boxShadow:'0 12px 48px rgba(0,0,0,0.6)' }}>
            <div style={{ fontSize:56, marginBottom:12 }}>👋</div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'white', marginBottom:10 }}>Are you still there?</h2>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.7)', lineHeight:1.6, marginBottom:24 }}>
              We paused your section timer because we didn't see any activity. Click below to continue where you left off.
            </p>
            <button className="btn btn-primary btn-lg" style={{ background:'#b39e89', border:'none', minWidth:200 }} onClick={handleStillThere}>
              Yes, I'm here →
            </button>
          </div>
        </div>
      )}

      {/* Session conflict overlay */}
      {sessionConflict && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ textAlign:'center', color:'white', maxWidth:460, padding:40 }}>
            <div style={{ fontSize:56, marginBottom:16 }}>⚠️</div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, marginBottom:12 }}>Session Conflict</h2>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.7)', lineHeight:1.7, marginBottom:24 }}>
              You can only have one course open at a time. This session was ended because another course or tab was opened. Please close other tabs and reload to continue.
            </p>
            <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
              <button className="btn btn-primary" onClick={() => window.location.reload()} style={{ background:'#b39e89', border:'none' }}>
                Reload This Tab
              </button>
              <button className="btn btn-ghost" onClick={() => navigate('/my-courses')} style={{ color:'white', borderColor:'rgba(255,255,255,0.3)' }}>
                Go to My Courses
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div style={styles.topBar}>
        <div style={styles.topLeft}>
          <button style={styles.topBtn} onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <Link to="/my-courses" style={styles.topHomeLink} className="hide-mobile-sm">← My Courses</Link>
          <Link to="/my-courses" style={styles.topHomeLink} className="show-mobile-sm">←</Link>
          <span style={styles.topTitle}>{course?.title}</span>
        </div>
        <div style={styles.topRight}>
          <span style={styles.progressLabel} className="hide-mobile-sm">{completedSlides}/{slides.length} slides</span>
          <div style={styles.progressBarSmall}>
            <div style={{ ...styles.progressFillSmall, width: `${progress}%` }} />
          </div>
          <span style={styles.progressPct}>{progress}%</span>
        </div>
      </div>

      <div style={styles.body}>
        {/* Sidebar backdrop for mobile — tap to close */}
        {sidebarOpen && window.innerWidth <= 768 && (
          <div style={styles.sidebarBackdrop} onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        {sidebarOpen && (
          <div style={styles.sidebar}>
            <div style={styles.sidebarHeader}>
              <span>Course Content</span>
              <button style={styles.sidebarClose} onClick={() => setSidebarOpen(false)}>✕</button>
            </div>
            <div style={styles.slideList}>
              {(() => {
                // Group slides by section
                const groups = [];
                let currentGroup = null;
                slides.forEach((slide, idx) => {
                  const secId = slide.section_id || '__none__';
                  if (!currentGroup || currentGroup.sectionId !== secId) {
                    currentGroup = {
                      sectionId: secId,
                      sectionTitle: slide.section_title || null,
                      slides: [],
                    };
                    groups.push(currentGroup);
                  }
                  currentGroup.slides.push({ slide, idx });
                });

                return groups.map((group) => {
                  const secId = group.sectionId;
                  const isExpanded = expandedSections[secId] !== false; // default expanded for first load
                  const locked = !isReviewMode && secId !== '__none__' && !isSectionUnlocked(secId);
                  const secDone = isReviewMode || (secId !== '__none__' && sectionComplete[secId]);
                  const sectionQuiz = secId !== '__none__' ? sectionQuizMap[secId] : null;
                  const quizPassed = sectionQuiz ? !!passedQuizzes[sectionQuiz.id] : true;

                  return (
                    <div key={secId}>
                      {/* Section header */}
                      {group.sectionTitle && (
                        <button
                          style={{ ...styles.sectionHeader, ...(locked ? { opacity: 0.4 } : {}) }}
                          onClick={() => setExpandedSections(prev => ({ ...prev, [secId]: !isExpanded }))}
                        >
                          <div style={styles.sectionHeaderLeft}>
                            <span style={{ ...styles.sectionArrow, transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                            <span style={styles.sectionHeaderTitle}>
                              {locked && '🔒 '}{group.sectionTitle}
                            </span>
                          </div>
                          <span style={{ fontSize: 11, color: secDone ? '#4ade80' : locked ? 'var(--slate-600)' : 'var(--slate-500)' }}>
                            {secDone ? '✓ Complete' : `${group.slides.length} slides`}
                          </span>
                        </button>
                      )}

                      {/* Slides in this section (collapsible) */}
                      {(isExpanded || !group.sectionTitle) && group.slides.map(({ slide, idx: slideIdx }) => {
                        const tdata = timers[slide.id];
                        const done = tdata?.is_complete;
                        const active = slideIdx === currentIdx;
                        const slideIsLast = slide.section_id && (slideIdx === slides.length - 1 || slides[slideIdx + 1]?.section_id !== slide.section_id);
                        const hasQuiz = slideIsLast && !!sectionQuiz;
                        return (
                          <button key={slide.id} style={{ ...styles.slideItem, ...(active ? styles.slideItemActive : {}), ...(done ? styles.slideItemDone : {}), ...(locked ? styles.slideItemLocked : {}), paddingLeft: group.sectionTitle ? 24 : 12 }} onClick={() => goToSlide(slideIdx)}>
                            <span style={{ ...styles.slideIndicator, ...(done ? styles.slideIndicatorDone : active ? styles.slideIndicatorActive : {}), ...(locked ? styles.slideIndicatorLocked : {}) }}>
                              {locked ? '🔒' : done ? '✓' : slideIdx + 1}
                            </span>
                            <div style={styles.slideItemContent}>
                              <div style={styles.slideItemTitle}>{slide.title || `Slide ${slideIdx + 1}`}</div>
                              {hasQuiz && <div style={{ fontSize:11, color: quizPassed ? '#4ade80' : '#fbbf24', marginTop:2 }}>
                                {quizPassed ? '✓ Quiz passed' : '📝 Quiz required'}
                              </div>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                });
              })()}
              {finalExam && (
                <div style={{ ...styles.slideItem, opacity: 0.7 }}>
                  <span style={styles.slideIndicator}>🎓</span>
                  <div style={styles.slideItemContent}>
                    <div style={styles.slideItemTitle}>Final Exam</div>
                    <div style={{ fontSize:11, color:'#fbbf24' }}>
                      {finalExam.final_exam_draw_count || finalExam.question_count || 25} questions
                      {' • '}
                      {finalExam.passing_score || 75}% to pass
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main content */}
        <div style={styles.main}>
          {showFinalExam ? (
            <div style={styles.slideContent}>
              {finalResult ? (
                <FinalExamResult result={finalResult} />
              ) : isReviewMode ? (
                <FinalExamReview
                  exam={finalExam}
                  onBack={() => setShowFinalExam(false)}
                />
              ) : (
                <FinalExamView
                  exam={finalExam}
                  responses={finalResponses}
                  setResponses={setFinalResponses}
                  onSubmit={submitFinalExam}
                  loading={finalLoading}
                  onBack={() => setShowFinalExam(false)}
                />
              )}
            </div>
          ) : showQuiz ? (
            <div style={styles.slideContent}>
              {quizResult ? (
                <QuizResult
                  result={quizResult}
                  onRetry={() => { setQuizResult(null); setQuizResponses({}); }}
                  onContinue={() => { setShowQuiz(false); if (currentIdx < slides.length - 1) goToSlide(currentIdx + 1); }}
                />
              ) : (
                <QuizView
                  quiz={quizData}
                  responses={quizResponses}
                  setResponses={setQuizResponses}
                  onSubmit={submitQuiz}
                  loading={quizLoading}
                  onBack={() => setShowQuiz(false)}
                />
              )}
            </div>
          ) : currentSlide ? (
            <>
              <div style={styles.slideContent}>
                <div style={styles.slideHeader}>
                  {!isReviewMode && currentSlide.section_id && sectionMinSeconds > 0 && (
                    <div style={{ ...styles.timerBadge, ...(sectionTimeComplete ? styles.timerBadgeDone : {}), ...(timerPaused ? { background:'rgba(251,191,36,0.15)', color:'#fbbf24', borderColor:'rgba(251,191,36,0.35)' } : {}) }}>
                      {sectionTimeComplete
                        ? '✓ Section Time Complete'
                        : timerPaused
                          ? '⏸ Timer paused — move your mouse to resume'
                          : `⏱ ${formatMinSec(sectionTimeRemaining)} remaining in section`}
                    </div>
                  )}
                  {isReviewMode && (
                    <div style={{ ...styles.timerBadge, background:'rgba(34,197,94,0.15)', color:'#4ade80', borderColor:'rgba(34,197,94,0.3)' }}>
                      ✓ Review Mode — Course Completed
                    </div>
                  )}
                </div>

                {/* Slide media (image / video / HTML content) — hidden on interactive-question
                    slides because the question stem is authored INTO the deck image, and
                    showing both the image AND the engagement widget below duplicates the
                    same question with the same options. */}
                {!currentSlide.interactive_question_id && currentSlide.video_url && (
                  <div style={styles.videoWrap}>
                    <iframe src={currentSlide.video_url} style={styles.video} allowFullScreen title={currentSlide.title} />
                  </div>
                )}

                {!currentSlide.interactive_question_id && currentSlide.image_url && !currentSlide.video_url && (
                  <img src={currentSlide.image_url} alt={currentSlide.title} style={styles.slideImage} />
                )}

                {!currentSlide.interactive_question_id && currentSlide.content && (
                  <div style={styles.slideBody} dangerouslySetInnerHTML={{ __html: currentSlide.content }} />
                )}

                {/* Interactive engagement question embedded on the slide (Kansas KREC) */}
                {currentSlide.interactive_question_id && enrollment && (
                  <InteractiveQuestion
                    questionId={currentSlide.interactive_question_id}
                    userCourseId={enrollment.id}
                    onAnswered={() => {
                      setInteractiveAnswered(prev => ({ ...prev, [currentSlide.interactive_question_id]: true }));
                    }}
                  />
                )}

                {/* Quiz prompt: last slide in section, quiz not passed — can take anytime */}
                {!isReviewMode && isLastSlideInSection && currentQuizId && !passedQuizzes[currentQuizId] && (
                  <div style={styles.quizPrompt}>
                    <div>
                      <strong style={{ color:'white' }}>📝 Section Quiz</strong>
                      <p style={{ fontSize:14, color:'rgba(255,255,255,0.7)', marginTop:4 }}>
                        Pass the section quiz ({currentSectionQuiz?.passing_score || 80}%) to complete this section.
                      </p>
                    </div>
                    <button className="btn btn-primary" onClick={() => loadQuiz(currentQuizId)} disabled={quizLoading} style={{ background:'#b39e89', border:'none' }}>
                      {quizLoading ? 'Loading...' : 'Take Section Quiz →'}
                    </button>
                  </div>
                )}

                {/* Quiz passed but time not met — countdown */}
                {!isReviewMode && isLastSlideInSection && currentQuizId && passedQuizzes[currentQuizId] && !sectionTimeComplete && sectionMinSeconds > 0 && (
                  <div style={{ ...styles.quizPrompt, background:'rgba(251,191,36,0.15)', borderColor:'rgba(251,191,36,0.3)' }}>
                    <div>
                      <strong style={{ color:'#fbbf24' }}>⏱ {formatMinSec(sectionTimeRemaining)} remaining in this section</strong>
                      <p style={{ fontSize:14, color:'rgba(255,255,255,0.7)', marginTop:4 }}>
                        Quiz passed! Continue reviewing the slides — the next section will unlock when the time requirement is met.
                        Stay active on this page (the timer pauses if you go idle).
                      </p>
                    </div>
                  </div>
                )}

                {/* Quiz passed AND time met — good to go */}
                {!isReviewMode && isLastSlideInSection && currentQuizId && passedQuizzes[currentQuizId] && timerComplete && (
                  <div style={{ ...styles.quizPrompt, background:'rgba(34,197,94,0.15)', borderColor:'rgba(34,197,94,0.3)' }}>
                    <strong style={{ color:'#4ade80' }}>✓ Section Complete!</strong>
                    <span style={{ fontSize:14, color:'rgba(255,255,255,0.7)' }}>Quiz passed and time requirement met. You may continue to the next section.</span>
                  </div>
                )}
              </div>

              {/* Nav */}
              <div style={styles.navBar}>
                <button className="btn btn-ghost" onClick={() => goToSlide(currentIdx - 1)} disabled={currentIdx === 0} style={{ color:'var(--slate-300)', borderColor:'var(--slate-600)' }}>
                  ← Previous
                </button>
                <span style={styles.navCounter}>{currentIdx + 1} / {slides.length}</span>
                {isReviewMode && currentIdx === slides.length - 1 && finalExam ? (
                  <button className="btn btn-primary" onClick={loadFinalExam} style={{ background:'#b39e89', border:'none' }}>
                    Review Final Exam 🎓
                  </button>
                ) : isReviewMode ? (
                  <button className="btn btn-primary" onClick={handleNext} disabled={currentIdx === slides.length - 1} style={{ background: currentIdx < slides.length - 1 ? 'var(--forest-500)' : 'var(--slate-600)', border:'none' }}>
                    Next →
                  </button>
                ) : currentIdx === slides.length - 1 && finalExam ? (
                  <button className="btn btn-primary" onClick={handleNext} disabled={!canGoNext} style={{ background: canGoNext ? '#b39e89' : 'var(--slate-600)', border:'none' }}>
                    {!canGoNext ? '⏱ Review Required' : 'Take Final Exam 🎓'}
                  </button>
                ) : isLastSlideInSection && currentQuizId && !passedQuizzes[currentQuizId] ? (
                  <button className="btn btn-primary" onClick={handleNext} style={{ background:'#b39e89', border:'none' }}>
                    📝 Take Quiz
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleNext} disabled={!canGoNext || currentIdx === slides.length - 1} style={{ background: canGoNext && currentIdx < slides.length - 1 ? 'var(--forest-500)' : 'var(--slate-600)', border:'none' }}>
                    {isLastSlideInSection && !timerComplete ? '⏱ Review Required' : 'Next →'}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding:60, textAlign:'center', color:'var(--slate-400)' }}>
              <p>No slides available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuizView({ quiz, responses, setResponses, onSubmit, loading, onBack }) {
  const answered = Object.keys(responses).length;
  const total = quiz?.questions?.length || 0;
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'white' }}>{quiz?.quiz?.title || 'Section Quiz'}</h2>
          <p style={{ color:'var(--slate-400)', fontSize:14, marginTop:4 }}>
            Passing score: {quiz?.quiz?.passing_score || 80}% • {answered}/{total} answered
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" style={{ color:'var(--slate-400)', borderColor:'var(--slate-600)' }} onClick={onBack}>← Back</button>
      </div>
      {quiz?.questions?.map((q, qi) => (
        <div key={q.id} style={styles.questionBlock}>
          <p style={styles.questionText}><span style={styles.questionNum}>{qi + 1}.</span> {q.question_text}</p>
          <div style={styles.optionsList}>
            {q.options?.map(opt => (
              <label key={opt.id} style={{ ...styles.optionLabel, ...(responses[q.id] === opt.id ? styles.optionSelected : {}) }}>
                <input type="radio" name={q.id} value={opt.id} checked={responses[q.id] === opt.id}
                  onChange={() => setResponses(prev => ({ ...prev, [q.id]: opt.id }))}
                  style={{ accentColor:'var(--teal-400)' }} />
                {opt.text}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button className="btn btn-primary btn-lg" style={{ width:'100%', marginTop:24, background:'#b39e89', border:'none' }}
        onClick={onSubmit} disabled={loading || answered < total}>
        {loading ? 'Submitting...' : `Submit Quiz (${answered}/${total} answered)`}
      </button>
    </div>
  );
}

function QuizResult({ result, onRetry, onContinue }) {
  const [showReview, setShowReview] = useState(false);
  return (
    <div style={{ padding:'32px 24px' }}>
      <div style={{ textAlign:'center', marginBottom: showReview ? 32 : 0 }}>
        <div style={{ fontSize:72, marginBottom:16 }}>{result.passed ? '🎉' : '😔'}</div>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:28, color:'white', marginBottom:8 }}>
          {result.passed ? 'Quiz Passed!' : 'Not Quite'}
        </h2>
        <div style={{ fontSize:56, fontWeight:800, color: result.passed ? '#4ade80' : '#f87171', margin:'16px 0' }}>
          {result.score}%
        </div>
        <p style={{ color:'var(--slate-400)', marginBottom:8 }}>
          You got {result.correct} out of {result.total} correct
        </p>
        <p style={{ color:'var(--slate-500)', fontSize:14, marginBottom:24 }}>
          Passing score: {result.passingScore}% • Attempt #{result.attemptNumber}
        </p>
        <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
          {!result.passed && (
            <>
              <button className="btn btn-ghost" style={{ color:'white', borderColor:'var(--slate-600)' }} onClick={onRetry}>
                Retry Quiz
              </button>
              <button className="btn btn-ghost" style={{ color:'#fbbf24', borderColor:'rgba(251,191,36,0.3)' }} onClick={() => setShowReview(!showReview)}>
                {showReview ? 'Hide Review' : 'Review Answers'}
              </button>
            </>
          )}
          {result.passed && (
            <button className="btn btn-primary" style={{ background:'#b39e89', border:'none' }} onClick={onContinue}>
              Continue →
            </button>
          )}
        </div>
      </div>
      {showReview && result.questionResults && (
        <QuestionReview questions={result.questionResults} />
      )}
    </div>
  );
}

function FinalExamView({ exam, responses, setResponses, onSubmit, loading, onBack }) {
  const questions = exam?.questions || [];
  const answered = Object.keys(responses).length;
  const total = questions.length;
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:24, color:'white' }}>🎓 Final Exam</h2>
          <p style={{ color:'var(--slate-400)', fontSize:14, marginTop:4 }}>
            {total} questions • Passing score: {exam?.passing_score || 75}% • {answered}/{total} answered
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" style={{ color:'var(--slate-400)', borderColor:'var(--slate-600)' }} onClick={onBack}>← Back</button>
      </div>
      {questions.map((q, qi) => (
        <div key={q.id} style={styles.questionBlock}>
          <p style={styles.questionText}><span style={styles.questionNum}>{qi + 1}.</span> {q.question_text}</p>
          <div style={styles.optionsList}>
            {q.options?.map(opt => (
              <label key={opt.id} style={{ ...styles.optionLabel, ...(responses[q.id] === opt.id ? styles.optionSelected : {}) }}>
                <input type="radio" name={q.id} value={opt.id} checked={responses[q.id] === opt.id}
                  onChange={() => setResponses(prev => ({ ...prev, [q.id]: opt.id }))}
                  style={{ accentColor:'var(--teal-400)' }} />
                {opt.text}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button className="btn btn-primary btn-lg" style={{ width:'100%', marginTop:24, background:'#b39e89', border:'none' }}
        onClick={onSubmit} disabled={loading || answered < total}>
        {loading ? 'Submitting...' : `Submit Final Exam (${answered}/${total} answered)`}
      </button>
    </div>
  );
}

// Read-only review of the final exam for completed courses — shows correct answers
function FinalExamReview({ exam, onBack }) {
  const questions = exam?.questions || [];
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:24, color:'white' }}>🎓 Final Exam — Review</h2>
          <p style={{ color:'var(--slate-400)', fontSize:14, marginTop:4 }}>
            You've already passed this exam. Correct answers are highlighted below.
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" style={{ color:'var(--slate-400)', borderColor:'var(--slate-600)' }} onClick={onBack}>← Back to Course</button>
      </div>
      {questions.map((q, qi) => (
        <div key={q.id} style={styles.questionBlock}>
          <p style={styles.questionText}><span style={styles.questionNum}>{qi + 1}.</span> {q.question_text}</p>
          <div style={styles.optionsList}>
            {q.options?.map(opt => (
              <div key={opt.id} style={{
                ...styles.optionLabel,
                background: opt.is_correct ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.03)',
                borderColor: opt.is_correct ? 'rgba(34,197,94,0.5)' : 'var(--slate-700)',
                color: opt.is_correct ? '#4ade80' : 'var(--slate-300)',
                fontWeight: opt.is_correct ? 600 : 400,
                cursor: 'default',
              }}>
                {opt.is_correct ? '✓ ' : ''}{opt.text}
              </div>
            ))}
          </div>
          {q.explanation && (
            <div style={{ marginTop:8, padding:'10px 14px', background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.25)', borderRadius:8, fontSize:13, color:'#93c5fd' }}>
              <strong>Explanation:</strong> {q.explanation}
            </div>
          )}
        </div>
      ))}
      <button className="btn btn-ghost btn-lg" style={{ width:'100%', marginTop:24, color:'var(--slate-300)', borderColor:'var(--slate-600)' }} onClick={onBack}>
        ← Back to Course
      </button>
    </div>
  );
}

function FinalExamResult({ result }) {
  const [showReview, setShowReview] = useState(false);
  return (
    <div style={{ padding:'32px 24px' }}>
      <div style={{ textAlign:'center', marginBottom: showReview ? 32 : 0 }}>
        <div style={{ fontSize:80, marginBottom:16 }}>{result.passed ? '🏆' : '😔'}</div>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:32, color:'white', marginBottom:8 }}>
          {result.passed ? 'Congratulations!' : 'Almost There'}
        </h2>
        <div style={{ fontSize:64, fontWeight:800, color: result.passed ? '#4ade80' : '#f87171', margin:'16px 0' }}>
          {result.score}%
        </div>
        <p style={{ color:'var(--slate-400)', marginBottom:8 }}>
          You got {result.correct} out of {result.total} correct
        </p>
        <p style={{ color:'var(--slate-500)', fontSize:14, marginBottom:24 }}>
          Passing score: {result.passingScore}% • Attempt #{result.attemptNumber}
        </p>
        {result.passed ? (
          <div>
            <div style={{ background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:12, padding:'20px 24px', marginBottom:24 }}>
              <p style={{ color:'#4ade80', fontWeight:700, fontSize:16 }}>🎓 Course Complete!</p>
              <p style={{ color:'var(--slate-300)', fontSize:14, marginTop:8 }}>Your certificate of completion has been recorded. Check your email for your certificate.</p>
            </div>
            <Link to="/my-courses" className="btn btn-primary" style={{ background:'#b39e89', border:'none' }}>
              Go to My Courses →
            </Link>
          </div>
        ) : (
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button className="btn btn-ghost" style={{ color:'#fbbf24', borderColor:'rgba(251,191,36,0.3)' }} onClick={() => setShowReview(!showReview)}>
              {showReview ? 'Hide Review' : 'Review Answers'}
            </button>
          </div>
        )}
      </div>
      {showReview && result.questionResults && (
        <QuestionReview questions={result.questionResults} />
      )}
    </div>
  );
}

function QuestionReview({ questions }) {
  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontFamily:'var(--font-display)', fontSize:18, color:'white', marginBottom:16 }}>Answer Review</h3>
      {questions.map((q, qi) => (
        <div key={q.questionId} style={{ background: q.isCorrect ? 'rgba(34,197,94,0.08)' : 'rgba(248,113,113,0.08)', border: `1px solid ${q.isCorrect ? 'rgba(34,197,94,0.2)' : 'rgba(248,113,113,0.2)'}`, borderRadius:12, padding:'16px 20px', marginBottom:12 }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:12 }}>
            <span style={{ fontSize:18, flexShrink:0 }}>{q.isCorrect ? '✅' : '❌'}</span>
            <p style={{ fontSize:15, color:'white', lineHeight:1.5, margin:0 }}>
              <span style={{ fontWeight:700, color:'#b39e89', marginRight:6 }}>{qi + 1}.</span>
              {q.questionText}
            </p>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6, marginLeft:28 }}>
            {q.options.map(opt => {
              const isSelected = opt.id === q.selectedOptionId;
              const isCorrectAnswer = opt.isCorrect;
              let bg = 'transparent';
              let border = '1px solid var(--slate-700)';
              let color = 'var(--slate-400)';
              if (isCorrectAnswer) {
                bg = 'rgba(34,197,94,0.15)';
                border = '1.5px solid rgba(34,197,94,0.5)';
                color = '#4ade80';
              } else if (isSelected && !isCorrectAnswer) {
                bg = 'rgba(248,113,113,0.15)';
                border = '1.5px solid rgba(248,113,113,0.5)';
                color = '#f87171';
              }
              return (
                <div key={opt.id} style={{ padding:'10px 14px', borderRadius:8, background:bg, border, color, fontSize:14, display:'flex', alignItems:'center', gap:10 }}>
                  {isCorrectAnswer && <span style={{ fontSize:12, fontWeight:700 }}>✓ Correct</span>}
                  {isSelected && !isCorrectAnswer && <span style={{ fontSize:12, fontWeight:700 }}>✗ Your answer</span>}
                  <span>{opt.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = {
  root:{ height:'100vh', display:'flex', flexDirection:'column', background:'var(--slate-900)', color:'white' },
  topBar:{ height:48, background:'var(--slate-800)', borderBottom:'1px solid var(--slate-700)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 12px', flexShrink:0, gap:8 },
  topLeft:{ display:'flex', alignItems:'center', gap:8, minWidth:0, flex:1 },
  topBtn:{ background:'none', border:'none', color:'var(--slate-400)', fontSize:18, cursor:'pointer', padding:'4px 8px', borderRadius:6, flexShrink:0 },
  topHomeLink:{ color:'var(--slate-400)', fontSize:13, textDecoration:'none', flexShrink:0 },
  topTitle:{ fontSize:13, fontWeight:600, color:'var(--slate-200)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', minWidth:0 },
  topRight:{ display:'flex', alignItems:'center', gap:8, flexShrink:0 },
  progressLabel:{ fontSize:11, color:'var(--slate-400)', whiteSpace:'nowrap' },
  progressBarSmall:{ width:80, height:5, background:'var(--slate-600)', borderRadius:999, overflow:'hidden' },
  progressFillSmall:{ height:'100%', background:'#b39e89', borderRadius:999, transition:'width 0.5s' },
  progressPct:{ fontSize:12, fontWeight:700, color:'#b39e89', minWidth:36 },
  body:{ flex:1, display:'flex', overflow:'hidden' },
  sidebar:{ width: window.innerWidth <= 768 ? '100%' : 280, maxWidth: 320, background:'var(--slate-800)', borderRight:'1px solid var(--slate-700)', display:'flex', flexDirection:'column', overflow:'hidden', flexShrink:0, position: window.innerWidth <= 768 ? 'absolute' : 'relative', zIndex: window.innerWidth <= 768 ? 50 : 1, top:0, bottom:0 },
  sidebarBackdrop:{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', zIndex:40 },
  sidebarHeader:{ padding:'16px 20px', fontSize:12, fontWeight:700, color:'var(--slate-400)', textTransform:'uppercase', letterSpacing:'0.06em', borderBottom:'1px solid var(--slate-700)', display:'flex', alignItems:'center', justifyContent:'space-between' },
  sidebarClose:{ background:'none', border:'none', color:'var(--slate-400)', fontSize:16, cursor:'pointer', padding:'4px 8px', borderRadius:4, lineHeight:1 },
  slideList:{ overflowY:'auto', flex:1, padding:'8px' },
  sectionHeader:{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, padding:'10px 12px', background:'rgba(255,255,255,0.03)', border:'none', borderBottom:'1px solid var(--slate-700)', color:'var(--slate-300)', cursor:'pointer', textAlign:'left', marginTop:4 },
  sectionHeaderLeft:{ display:'flex', alignItems:'center', gap:8, minWidth:0 },
  sectionArrow:{ fontSize:10, color:'var(--slate-500)', transition:'transform 0.2s', flexShrink:0 },
  sectionHeaderTitle:{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  slideItem:{ width:'100%', display:'flex', alignItems:'flex-start', gap:12, padding:'10px 12px', borderRadius:8, background:'none', border:'none', color:'var(--slate-300)', cursor:'pointer', textAlign:'left', transition:'background 0.15s', marginBottom:2 },
  slideItemActive:{ background:'rgba(179,158,137,0.15)', color:'white' },
  slideItemDone:{ opacity:0.7 },
  slideItemLocked:{ opacity:0.4, cursor:'not-allowed' },
  slideIndicatorLocked:{ background:'var(--slate-700)', fontSize:10 },
  slideIndicator:{ width:24, height:24, borderRadius:'50%', background:'var(--slate-600)', color:'white', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 },
  slideIndicatorDone:{ background:'#16a34a' },
  slideIndicatorActive:{ background:'#b39e89', boxShadow:'0 0 0 2px rgba(179,158,137,0.4)' },
  slideItemContent:{ flex:1, minWidth:0 },
  slideItemTitle:{ fontSize:13, lineHeight:1.4, overflow:'hidden', textOverflow:'ellipsis' },
  main:{ flex:1, overflow:'auto', display:'flex', flexDirection:'column' },
  slideContent:{ flex:1, padding: window.innerWidth <= 480 ? '16px 12px' : '32px 40px', maxWidth:900, width:'100%', margin:'0 auto' },
  slideHeader:{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, marginBottom:28 },
  slideTitle:{ fontFamily:'var(--font-display)', fontSize:'clamp(1.3rem, 2.5vw, 1.9rem)', color:'white', lineHeight:1.25 },
  timerBadge:{ flexShrink:0, background:'rgba(234,179,8,0.15)', border:'1px solid rgba(234,179,8,0.3)', color:'#fbbf24', padding:'6px 14px', borderRadius:999, fontSize:13, fontWeight:600, whiteSpace:'nowrap' },
  timerBadgeDone:{ background:'rgba(22,163,74,0.15)', borderColor:'rgba(22,163,74,0.3)', color:'#4ade80' },
  videoWrap:{ position:'relative', paddingTop:'56.25%', marginBottom:28, borderRadius:12, overflow:'hidden', background:'var(--slate-800)' },
  video:{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' },
  slideImage:{ width:'100%', borderRadius:12, marginBottom:28, maxHeight:'none', objectFit:'contain' },
  slideBody:{ fontSize:16, lineHeight:1.8, color:'var(--slate-200)', marginBottom:32 },
  quizPrompt:{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, background:'rgba(179,158,137,0.1)', border:'1px solid rgba(179,158,137,0.3)', borderRadius:12, padding:'20px 24px', marginTop:32 },
  navBar:{ padding: window.innerWidth <= 480 ? '12px' : '16px 40px', borderTop:'1px solid var(--slate-700)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--slate-800)', flexShrink:0, gap:8 },
  navCounter:{ fontSize:14, color:'var(--slate-400)' },
  questionBlock:{ background:'var(--slate-800)', borderRadius:12, padding:'20px 24px', marginBottom:16, border:'1px solid var(--slate-700)' },
  questionText:{ fontSize:16, color:'white', lineHeight:1.5, marginBottom:16 },
  questionNum:{ fontWeight:700, color:'#b39e89', marginRight:8 },
  optionsList:{ display:'flex', flexDirection:'column', gap:10 },
  optionLabel:{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:8, border:'1.5px solid var(--slate-600)', cursor:'pointer', fontSize:15, color:'var(--slate-200)', transition:'all 0.15s' },
  optionSelected:{ borderColor:'#b39e89', background:'rgba(179,158,137,0.15)', color:'white' },
};